import { useEffect, useState } from 'react';
import ICAL from 'ical.js';

const normalizeDate = (date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

// Convertit une ICAL.Time en Date locale sans jamais dépendre du fuseau horaire.
// Pour les événements "journée entière" (VALUE=DATE), on lit directement les
// composantes année/mois/jour : c'est ce qui garantit qu'un DTEND (= jour de
// sortie) reste exactement sur le bon jour, quel que soit l'endroit d'où l'app
// est ouverte. On évite ainsi le décalage de -1 jour qui pouvait se produire via
// toJSDate() dans un fuseau derrière UTC.
const icalTimeToLocalDate = (icalTime) => {
  if (icalTime && typeof icalTime.year === 'number' && typeof icalTime.day === 'number') {
    return new Date(icalTime.year, (icalTime.month || 1) - 1, icalTime.day, 0, 0, 0, 0);
  }
  return normalizeDate(icalTime.toJSDate());
};

const parseIcsRanges = (icsText) => {
  const jcalData = ICAL.parse(icsText);
  const comp = new ICAL.Component(jcalData);
  const events = comp.getAllSubcomponents('vevent');

  return events.map((event) => {
    const icalEvent = new ICAL.Event(event);
    const sourceId =
      icalEvent.component.getFirstPropertyValue('x-villa-source-id') ||
      icalEvent.component.getFirstPropertyValue('X-VILLA-SOURCE-ID') ||
      null;
    const sourceLabel =
      icalEvent.component.getFirstPropertyValue('x-villa-source') ||
      icalEvent.component.getFirstPropertyValue('X-VILLA-SOURCE') ||
      icalEvent.component.getFirstPropertyValue('x-villa-source'.toUpperCase()) ||
      null;
    const kind =
      icalEvent.component.getFirstPropertyValue('x-villa-kind') ||
      icalEvent.component.getFirstPropertyValue('X-VILLA-KIND') ||
      null;

    return {
      start: icalTimeToLocalDate(icalEvent.startDate),
      end: icalTimeToLocalDate(icalEvent.endDate),
      sourceId: typeof sourceId === 'string' && sourceId.trim() ? sourceId.trim().toLowerCase() : null,
      sourceLabel: typeof sourceLabel === 'string' && sourceLabel.trim() ? sourceLabel : null,
      // 'reservation' (client réel) ou 'blocked' (buffer/fermeture manuelle sans
      // client, injecté par la plateforme). Absent (flux sans le tag) = traité
      // comme une réservation, pour ne rien casser côté disponibilité publique.
      kind: kind === 'blocked' ? 'blocked' : 'reservation',
    };
  });
};

export default function useIcalUnavailableRanges({ withSources = false, enabled = true } = {}) {
  const [ranges, setRanges] = useState([]);
  const [isLoading, setIsLoading] = useState(Boolean(enabled));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      setError('');
      return;
    }

    let isCancelled = false;

    const fetchIcs = async () => {
      try {
        setIsLoading(true);
        setError('');

        const url = withSources
          ? '/calendar-proxied-for-cors.php?withSources=1'
          : '/calendar-proxied-for-cors.php';

        const res = await fetch(url);
        const text = await res.text();
        const parsed = parseIcsRanges(text);

        if (!isCancelled) {
          setRanges(parsed);
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err?.message || 'Erreur lors du chargement du calendrier.');
          setRanges([]);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchIcs();

    return () => {
      isCancelled = true;
    };
  }, [withSources, enabled]);

  return { ranges, isLoading, error };
}
